import Tooltip from "@Components/Tooltip/Tooltip";
import SlashedPrice from "./SlashedPrice";
import PropertyDetailsModal from "@Pages/PropertyPage/components/PropertyDetailsModal";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import styles from "./SlashedPrice/SlashedPrice.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";

import Portal from "@Components/Portal";
import useTranslationHook from "hooks/useTranslationHook";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import useIsLanguage from "hooks/useIsLanguage";
import { getTranslatedDurationValue } from "../utils";
import CircleInfo from "@Icons/CircleInfo";

interface PriceDisplayProps {
  price?: string;
  slashedPrice: any;
  isSoldOut: boolean;
  onPriceClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
  showModal: boolean;
  onCloseModal: (e: Event) => void;
  isDesktopModal?: boolean;
  slashedPriceState?: boolean;
  tenancy?: any;
  showOwnPriceComp?: boolean;
  ownPriceComp?: any;
  toolTipPosition?: string;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price = "",
  slashedPrice,
  isSoldOut,
  onPriceClick,
  showModal,
  onCloseModal,
  isDesktopModal = false,
  slashedPriceState = false,
  showOwnPriceComp = false,
  ownPriceComp,
  toolTipPosition = "",
}) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const { isBelow } = useResponsiveness();
  const language = useIsLanguage();
  const isBelowMD = isBelow(SCREEN_SIZE.MD);
  const handlePriceClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (isDesktop) return;
    e.stopPropagation();
    onPriceClick(e);
  };
  const translatedPrice = getTranslatedDurationValue(price, t, "/");

  const PriceComp = () => (
    <span
      data-testid="property-room-type-tenancy-pricing"
      className={formatClassNames(styles.price, isSoldOut && styles.price_soldOut)}
      onClick={handlePriceClick}
      role="none"
    >
      {slashedPrice.is_flexible ? (
        <>
          <span className={styles.cashbackText}>
            {t("propertypage.roomtypesectiondynamic.roomtypecard.cashback")}{" "}
            {slashedPrice?.currency}
            {slashedPrice.total_saving}{" "}
          </span>
          <u> {language !== "en" ? translatedPrice || price : price}</u>
        </>
      ) : (
        <>
          <span className={styles.originalPrice}>
            <s>{slashedPrice.only_price}</s>
          </span>{" "}
          {getTranslatedDurationValue(slashedPrice.effective_base_rent, t, "/") ||
            slashedPrice.effective_base_rent}
          <CircleInfo height={16} width={16} />
        </>
      )}
    </span>
  );

  if (
    (!slashedPrice?.offers_array?.length ||
      !slashedPriceState ||
      slashedPrice?.is_negative_effective_value) &&
    !showOwnPriceComp
  ) {
    return (
      <span
        data-testid="property-room-type-tenancy-pricing"
        className={formatClassNames(styles.price, isSoldOut && styles.price_soldOut)}
        role="none"
      >
        {language !== "en" ? translatedPrice : price}
      </span>
    );
  }
  if (isDesktop) {
    return (
      <>
        <Tooltip
          title={price}
          direction={toolTipPosition || (isDesktopModal || isBelowMD ? "top" : "top")}
          content={<SlashedPrice slashedPrice={slashedPrice} isDesktop={isDesktop} />}
          addComponent
          className={formatClassNames(
            styles.discountedPrice,
            isDesktopModal && styles.modalTooltip,
            toolTipPosition && styles.propertyPageTop,
          )}
        >
          {showOwnPriceComp ? ownPriceComp() : <PriceComp />}
        </Tooltip>
      </>
    );
  }
  // for mobile
  return (
    <>
      {showOwnPriceComp ? ownPriceComp() : <PriceComp />}
      {showModal && (
        <Portal rootId="forPortal">
          {/*
            React portals bubble synthetic events through the React tree, not the
            DOM tree. This modal is rendered as a descendant of TenancyCard's
            clickable container, so clicks inside the modal (e.g. the "Got it"
            button or the backdrop) would bubble up to the card's onClick and
            trigger a book action. That book action opens the lead-form modal
            while this modal's history entry is being popped via goBack, and the
            racing popstate instantly closes the lead-form modal (visible as a
            flicker in the app webview). Stop propagation here so modal
            interactions never reach the card.
          */}
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
          <div onClick={(e) => e.stopPropagation()}>
            <PropertyDetailsModal
              header={t("propertypage.roomtypesectiondynamic.roomtypecard.rentsummary")}
              actionButtonText={t("propertypage.roomtypesectiondynamic.roomtypecard.gotit")}
              bodyWithNoPadding
              hidePropertyModal={onCloseModal}
            >
              <div className={styles.discountedPrice}>
                <SlashedPrice slashedPrice={slashedPrice} isDesktop={isDesktop} />
              </div>
            </PropertyDetailsModal>
          </div>
        </Portal>
      )}
    </>
  );
};

export default wrapperHOC(PriceDisplay, {
  componentName: "PriceDisplay-TenancyCard-RoomTypeCard-RoomTypeSection-PropertyPage",
  showForChina: true,
});
